import React, { useState, useEffect } from 'react';
import ParticleBackground from './common/ParticleBackground';
import './Lobby.css';

export default function Lobby({ send, error, clearError, onBack, tunnelUrl, category = 'cards', authUsername }) {
  // Check for room code in URL on mount
  const params = new URLSearchParams(window.location.search);
  const roomFromUrl = params.get('room') || '';

  const [name, setName] = useState(authUsername || localStorage.getItem('game28_name') || '');
  const [roomId, setRoomId] = useState(roomFromUrl || '');
  
  // Steps: 'entry', 'create_name', 'select_mode', 'join'
  const [step, setStep] = useState(roomFromUrl ? 'join' : 'entry'); 

  const isCards = category === 'cards';

  // Auto-join effect: Only if we already HAVE a saved name
  useEffect(() => {
    const savedName = authUsername || localStorage.getItem('game28_name');
    if (roomFromUrl && savedName && step === 'join') {
      console.log("🚀 [Lobby] Auto-joining room:", roomFromUrl);
      send('join_room', { name: savedName.trim(), room_id: roomFromUrl.toUpperCase() });
      setStep('entry'); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomFromUrl, authUsername]); 

  const handleCreate = (selectedMode) => {
    const finalName = authUsername || name.trim();
    if (!finalName) return;
    if (!authUsername) localStorage.setItem('game28_name', finalName);
    send('create_room', { name: finalName, mode: selectedMode });
  };

  const handleJoin = () => {
    const trimmedRoomId = (roomId || '').trim();
    const finalName = authUsername || name.trim();
    if (!finalName || !trimmedRoomId) return;
    if (!authUsername) localStorage.setItem('game28_name', finalName);
    send('join_room', { name: finalName, room_id: trimmedRoomId.toUpperCase() });
  };

  // --- STEP 1: CHOOSE ACTION ---
  const renderEntry = () => (
    <div className="fade-in" key="entry-step">
      <div className="lobby-header-compact">
        <h2>{isCards ? 'The Cheetu Club' : 'The Board Room'}</h2>
        <p>Ready for a match?</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
        <button 
          className="btn btn-primary" 
          onClick={() => setStep(authUsername ? 'select_mode' : 'create_name')} 
          style={{ padding: '24px', fontSize: '1.2rem', height: 'auto' }}
        >
          Create New Room
        </button>
        
        <button 
          className="btn btn-secondary" 
          onClick={() => setStep('join')}
          style={{ padding: '24px', fontSize: '1.2rem', height: 'auto' }}
        >
          Join via Code
        </button>
      </div>

      {onBack && (
        <button className="btn-text-back" style={{ marginTop: '30px', width: '100%', textAlign: 'center' }} onClick={onBack}>
          ← Switch Game Category
        </button>
      )}
    </div>
  );

  // --- STEP 2: ENTER NAME (FOR CREATE) ---
  const renderCreateName = () => (
    <div className="fade-in">
      <div className="lobby-header-compact">
        <h2>Hosting a Game</h2>
        <p>First, what is your name?</p>
      </div>

      <div className="form-group">
        <label>Your Nickname</label>
        <input
          type="text"
          placeholder="e.g. Maverick"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) setStep('select_mode'); }}
          maxLength={16}
          autoFocus
        />
      </div>

      <button 
        className="btn btn-primary" 
        onClick={() => setStep('select_mode')} 
        disabled={!name.trim()}
      >
        Continue to Modes
      </button>

      <button className="btn-text-back" style={{ marginTop: '20px', width: '100%', textAlign: 'center' }} onClick={() => setStep('entry')}>
        ← Back
      </button>
    </div>
  );

  // --- STEP 3A: HERO CARD SELECTION ---
  const renderSelectMode = () => (
    <div className="fade-in">
      <div className="lobby-header-compact" style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h2>Select Game Mode</h2>
        <p>What are we playing today, {name}?</p>
      </div>

      <div className="hero-cards-wrapper">
        {isCards ? (
          <>
            {/* CARD 1: 28 */}
            <div className="hero-card" onClick={() => handleCreate('28')}>
              <div className="watermark">J♠</div>
              <div className="card-content">
                <h3 className="mode-name">28</h3>
                <p className="mode-lang">ഇരുപത്തെട്ട്</p>
                <div className="mode-meta">
                  <span>4 Players</span>
                  <span className="dot">•</span>
                  <span>Classic</span>
                </div>
              </div>
            </div>

            {/* CARD 2: 56 */}
            <div className="hero-card" onClick={() => handleCreate('56')}>
              <div className="watermark" style={{ color: 'rgba(239, 68, 68, 0.04)' }}>9♥</div>
              <div className="card-content">
                <h3 className="mode-name">56</h3>
                <p className="mode-lang">അമ്പത്താറ്</p>
                <div className="mode-meta">
                  <span>6 Players</span>
                  <span className="dot">•</span>
                  <span>Double Deck</span>
                </div>
              </div>
            </div>

            {/* CARD 3: DONKEY */}
            <div className="hero-card" onClick={() => handleCreate('donkey')}>
              <div className="watermark" style={{ color: 'rgba(255, 255, 255, 0.04)' }}>A♠</div>
              <div className="card-content">
                <h3 className="mode-name" style={{ fontSize: '2rem' }}>Donkey</h3>
                <p className="mode-lang">കഴുത</p>
                <div className="mode-meta">
                  <span>4-6 Players</span>
                  <span className="dot">•</span>
                  <span>Survival</span>
                </div>
              </div>
            </div>

            {/* CARD 4: POKER */}
            <div className="hero-card" onClick={() => handleCreate('poker')}>
              <div className="watermark" style={{ color: 'rgba(239, 68, 68, 0.04)' }}>A♥</div>
              <div className="card-content">
                <h3 className="mode-name" style={{ fontSize: '2rem' }}>Poker</h3>
                <p className="mode-lang">പോക്കർ</p>
                <div className="mode-meta">
                  <span>2-6 Players</span>
                  <span className="dot">•</span>
                  <span>Hold'em</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* CARD 1: LUDO */}
            <div className="hero-card" onClick={() => handleCreate('ludo')}>
              <div className="watermark" style={{ fontSize: '4rem', top: '10px' }}>🎲</div>
              <div className="card-content">
                <h3 className="mode-name">Ludo</h3>
                <p className="mode-lang">ലുഡോ</p>
                <div className="mode-meta">
                  <span>2-4 Players</span>
                  <span className="dot">•</span>
                  <span>Strategy</span>
                </div>
              </div>
            </div>

            {/* CARD 2: CHESS (Coming Soon) */}
            <div className="hero-card disabled">
              <div className="watermark" style={{ fontSize: '4rem', top: '10px' }}>♟</div>
              <div className="card-content">
                <h3 className="mode-name">Chess</h3>
                <p className="mode-lang">ചെസ്സ്</p>
                <div className="mode-meta">
                  <span>2 Players</span>
                  <span className="dot">•</span>
                  <span>Coming Soon</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <button className="btn-text-back" onClick={() => setStep('create_name')}>
        ← Back to Name
      </button>
    </div>
  );

  // --- STEP 3B: JOIN ROOM ---
  const renderJoin = () => (
    <div className="fade-in">
      <div className="lobby-header-compact">
        <h2>{roomFromUrl ? 'Join Table' : 'Join a Table'}</h2>
        <p>{roomFromUrl ? `Joining room ${roomFromUrl}` : 'Enter the code from your host'}</p>
      </div>

      {!authUsername && (
        <div className="form-group">
          <label>Your Name</label>
          <input
            type="text"
            placeholder="Enter nickname"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && name.trim() && roomId.trim()) handleJoin(); }}
            maxLength={16}
            autoFocus
          />
        </div>
      )}

      {!roomFromUrl && (
        <div className="form-group">
          <label>Room Code</label>
          <input
            type="text"
            placeholder="Enter 6-letter code"
            value={roomId}
            onChange={e => setRoomId(e.target.value.toUpperCase())}
            onKeyDown={e => { if (e.key === 'Enter' && name.trim() && roomId.trim()) handleJoin(); }}
            maxLength={6}
          />
        </div>
      )}

      <button 
        className="btn btn-primary" 
        onClick={handleJoin} 
        disabled={!(authUsername || name.trim()) || roomId.trim().length < 4}
      >
        Enter Room
      </button>

      {!roomFromUrl && (
        <button className="btn btn-secondary" style={{ marginTop: '12px' }} onClick={() => setStep('entry')}>
          ← Back
        </button>
      )}
    </div>
  );

  return (
    <div className="lobby-page" style={{ 
      background: 'radial-gradient(ellipse at center, #064e3b 0%, #022c22 100%)' 
    }}>
      <ParticleBackground category={category} />
      <div className="lobby-container">
        
        {/* LEFT SIDE: BRANDING */}
        <div className="lobby-branding">
          <div className="logo-wrapper">
            <img 
              src={isCards ? "/assets/resources/The-Cheetu-Club.svg" : "/assets/resources/The-Board-Game-Club.svg"} 
              alt={isCards ? "The Cheetu Club" : "The Board Room"} 
              className="main-logo" 
            />
            <div className="logo-glow" style={{ 
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.08) 0%, transparent 70%)'
            }}></div>
          </div>
        </div>

        {/* RIGHT SIDE: DYNAMIC ACTION PANEL */}
        <div className="lobby-actions">
          {/* We dynamically append 'wide-panel' to fit the 3 hero cards beautifully */}
          <div className={`lobby-box ${step === 'select_mode' ? 'wide-panel' : ''}`}>
            
            {error && (
              <div className="error-msg" onClick={clearError}>
                ⚠ {error} <span style={{ opacity: 0.6, marginLeft: 8 }}>(dismiss)</span>
              </div>
            )}

            {step === 'entry' && renderEntry()}
            {step === 'create_name' && renderCreateName()}
            {step === 'select_mode' && renderSelectMode()}
            {step === 'join' && renderJoin()}

          </div>
        </div>

      </div>
    </div>
  );
}
